import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Modal } from '../modal';
import { useEffect, useMemo, useState } from 'react';
import {
    addBookingSlot,
    deleteSlot as deleteSlotRequest,
    updateBookingSlot
} from 'api/booking';
import { ConfirmDeleteDialog } from 'components/confirmDeleteDialog';
import { BookingSlotForm } from './bookingSlotForm';
import {
    BookingActivity,
    BookingSlot,
    BookingSlotRequest
} from 'api-types/models/Booking';

import './index.css';
import { addMinutes, format } from 'date-fns';

interface IProps {
    date?: Date;
    setDate: (date?: Date) => void;
    closeModal: () => void;
    rooms: string[];
    selectedRoom?: string;
    addOrUpdateSlot: (
        slot: BookingSlot | BookingSlot[],
        update: boolean
    ) => void;
    deleteSlot: (slot: BookingSlot) => void;
    slot?: BookingSlot;
    activities: BookingActivity[];
    slotsPerWeek?: BookingSlot[];
}

export function AddBookingSlotModal({
    setDate,
    date,
    closeModal,
    selectedRoom,
    activities,
    rooms,
    addOrUpdateSlot,
    deleteSlot,
    slot,
    slotsPerWeek = []
}: Readonly<IProps>) {
    const { t } = useTranslation();

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [error, setError] = useState<string>();

    // When creating a new slot, detect overlapping existing slots to adjust start time and cap duration
    const { adjustedDate, maxDuration } = useMemo(() => {
        if (slot || !date) {
            return { adjustedDate: date, maxDuration: undefined };
        }

        const selectedTime = date.getTime();

        // Find slots on the same day that overlap with the selected time
        const overlapping = slotsPerWeek.filter((s) => {
            const slotStart = s.from;
            const slotEnd = addMinutes(slotStart, s.duration).getTime();
            // overlap: slotStart < selectedHourEnd AND slotEnd > selectedHourStart
            const hourStart = selectedTime;
            const hourEnd = addMinutes(selectedTime, 60).getTime();
            return slotStart < hourEnd && slotEnd > hourStart;
        });

        if (overlapping.length === 0) {
            return { adjustedDate: date, maxDuration: undefined };
        }

        // Slots that START before or at the selected time (new slot must start after them)
        const precedingSlots = overlapping.filter(
            (s) => s.from <= selectedTime
        );
        // Slots that START after the selected time (limit duration so we don't overlap them)
        const followingSlots = overlapping.filter((s) => s.from > selectedTime);

        let newStart = selectedTime;

        if (precedingSlots.length > 0) {
            // Use the latest end time among preceding slots as the new start

            newStart = Math.max(
                ...precedingSlots.map((s) =>
                    addMinutes(s.from, s.duration).getTime()
                )
            );
        }

        let maxDur: number | undefined;

        if (followingSlots.length > 0) {
            // Find the earliest start among following slots
            const earliestFollowingStart = Math.min(
                ...followingSlots.map((s) => s.from)
            );
            const availableMs = earliestFollowingStart - newStart;
            const availableMins = Math.floor(availableMs / 60000);
            maxDur = Math.max(availableMins, 0);
        }

        return {
            adjustedDate: new Date(newStart),
            maxDuration: maxDur
        };
    }, [date, slot, slotsPerWeek]);

    const form = useForm<BookingSlotRequest>({
        mode: 'all',
        defaultValues: slot
    });

    // Sync form fields with the selected slot.
    // react-hook-form's `defaultValues` is only applied on the very first mount.
    // If there is any timing gap between the modal mounting and the `slot` prop
    // arriving (e.g. native click listener fires before React's batched state
    // updates are committed), the form would stay empty.  Calling reset()
    // here guarantees the fields are always populated with the slot's values.
    useEffect(() => {
        if (slot) {
            form.reset(slot);
        }
    }, [slot?.slotId]);

    const {
        formState: { isValid },
        getValues,
        handleSubmit
    } = form;

    const handleAddSlot = async (slotBody: BookingSlotRequest) => {
        const effectiveDate = slot ? date : (adjustedDate ?? date);
        if (effectiveDate) {
            if (slot) {
                updateBookingSlot(
                    {
                        ...slotBody,
                        from: effectiveDate?.getTime() ?? 0,
                        duration: +slotBody.duration
                    },
                    slot.slotId
                ).then((response) => addOrUpdateSlot(response, true));
            } else if (slotBody.lastSlotStartTime) {
                try {
                    let startTime = effectiveDate.getTime();
                    let addedSlots: BookingSlot[] = [];

                    while (startTime <= slotBody.lastSlotStartTime) {
                        const slot = await addBookingSlot({
                            ...slotBody,
                            duration: +slotBody.duration,
                            from: startTime,
                            repeatOnDays: slotBody.repeatOnDays ?? [],
                            repeat: slotBody.repeatWeekly,
                            date: format(effectiveDate, 'yyyy-MM-dd'),
                            room: slotBody.room
                        });

                        if (slot.error) {
                            setError(slot.error);
                        } else {
                            addedSlots = addedSlots.concat(slot);
                        }
                        startTime = addMinutes(
                            new Date(startTime),
                            +slotBody.duration +
                                +slotBody.breakBetweenSlotsInMinutes
                        ).getTime();
                    }
                    addOrUpdateSlot(addedSlots, false);
                } catch (error) {
                    console.log(error);
                }
            } else {
                addBookingSlot({
                    ...slotBody,
                    duration: +slotBody.duration,
                    capacity: +slotBody.capacity,
                    from: effectiveDate.getTime(),
                    repeatOnDays: slotBody.repeatOnDays ?? [],
                    repeat: slotBody.repeatWeekly,
                    date: format(effectiveDate, 'yyyy-MM-dd'),
                    room: slotBody.room
                }).then((response) => {
                    if (response.error) {
                        setError(response.error.message);
                    } else {
                        addOrUpdateSlot(response, false);
                    }
                });
            }
        }
    };

    const handleDeleteSlot = (slot: BookingSlot) => {
        deleteSlotRequest(slot).then((response) => {
            if (response.error) {
                setError(response.error.message);
                setIsDeleteDialogOpen(false);
            } else {
                deleteSlot(slot);
            }
        });
    };

    return (
        <>
            <Modal
                title={slot ? 'update_slot' : 'add_slot'}
                closeModal={closeModal}
                content={
                    <BookingSlotForm
                        date={slot ? date : adjustedDate}
                        slot={slot}
                        setDate={setDate}
                        rooms={rooms}
                        activities={activities}
                        selectedRoom={selectedRoom}
                        form={form}
                        error={error}
                        handleAddSlot={handleAddSlot}
                        maxDuration={maxDuration}
                    />
                }
                actionButtons={
                    <>
                        <button
                            type='button'
                            className='btn btn-gray'
                            onClick={closeModal}
                        >
                            {t('cancel')}
                        </button>
                        {slot !== undefined && (
                            <button
                                type='button'
                                className='btn-danger'
                                onClick={() => setIsDeleteDialogOpen(true)}
                            >
                                {t('remove')}
                            </button>
                        )}
                        <button
                            className='btn btn-blue'
                            onClick={handleSubmit(handleAddSlot)}
                            disabled={
                                !isValid ||
                                getValues('activityId') === t('select_session')
                            }
                        >
                            {t('submit')}
                        </button>
                    </>
                }
            />
            {slot !== undefined && isDeleteDialogOpen && (
                <ConfirmDeleteDialog
                    onConfirm={() => handleDeleteSlot(slot)}
                    onCancel={() => setIsDeleteDialogOpen(false)}
                    content={<p>{t('delete-slot-warning')}</p>}
                />
            )}
        </>
    );
}
